import { forwardRef, type ReactElement, type ReactNode, type Ref, useCallback, useImperativeHandle, useRef } from "react";
import type { TextInput } from "react-native";
import { type GetProps, isWeb, styled, type TamaguiElement, Input as TInput } from "tamagui";
import { X } from "@tamagui/lucide-icons";
import { FieldShell, type FieldShellSlot, type FieldValidation } from "../FieldShell";
import { fieldTextVariants, useTextFieldShell } from "../FieldShell/useTextFieldShell";
import type { IconComponent } from "../Icon";
import { FieldIconButton } from "./FieldIconButton";
const StyledInput = styled(TInput, {
  name: "StyledInput",
  unstyled: true,
  fontFamily: "$text",
  fontSize: "$body1",
  fontWeight: "400",
  color: "$textDefault",
  width: "100%",
  padding: 0,
  margin: 0,
  backgroundColor: "transparent",
  borderWidth: 0,
  outlineWidth: 0,
  placeholderTextColor: "$textSubtle",
  selectionColor: "$bgBrandStrong",
  variants: fieldTextVariants,
  defaultVariants: {
    validation: "none"
  }
});
export type InputValidation = FieldValidation;

/**
 * Trailing-affordance variants, in precedence order at render time:
 * clearable-when-value → pressable icon → decorative `rightIcon`.
 * `rightIconLabel` is required alongside `onRightIconPress` so the icon-only
 * button always has an accessible name.
 */
type TrailingIconProps = {
  onRightIconPress: () => void;
  /** Accessible label for the pressable trailing icon (required). */
  rightIconLabel: string;
  /** The pressable button needs an icon to render — required in this arm. */
  rightIcon: IconComponent;
  /** Ref to the pressable trailing icon button. */
  rightIconRef?: Ref<TamaguiElement>;
  clearable?: never;
  onClear?: never;
  rightElement?: never;
} | {
  clearable: true;
  onClear?: () => void;
  rightIcon?: never;
  rightIconRef?: never;
  onRightIconPress?: never;
  rightIconLabel?: never;
  rightElement?: never;
} | {
  rightIcon?: IconComponent;
  rightIconRef?: never;
  onRightIconPress?: never;
  rightIconLabel?: never;
  clearable?: never;
  onClear?: never;
  rightElement?: never;
} | {
  rightElement: ReactElement;
  rightIcon?: never;
  rightIconRef?: never;
  onRightIconPress?: never;
  rightIconLabel?: never;
  clearable?: never;
  onClear?: never;
};
export type InputProps = Omit<GetProps<typeof StyledInput>, "size" | "disabled" | "onPress"> & {
  /** Label text displayed above the input value. Different from placeholder text. */
  label?: string;
  /** Marks the field required: renders an asterisk on the label + `aria-required`. */
  required?: boolean;
  helperText?: string;
  validation?: InputValidation;
  prefix?: string;
  /** Text after the value, or a node such as a badge. */
  suffix?: ReactNode;
  leftIcon?: IconComponent;
  /** Whether to show the character count (requires maxLength to be set) */
  showCharacterCount?: boolean;
  /** Opt-in 72px frame, matching prominent selection rows. */
  prominent?: boolean;
  /** `pill`: the 44px fully rounded search field. */
  shape?: "pill";
  disabled?: boolean;
  /** Press handler for the field frame, replacing the default (focus the
   *  control). For non-editable display triggers whose control is
   *  `pointerEvents="none"` — e.g. the Combobox sheet trigger. */
  onPress?: () => void;
} & TrailingIconProps;
export const Input = forwardRef<TextInput | null, InputProps>(function Input({
  label,
  required = false,
  helperText,
  validation = "none",
  prefix,
  suffix,
  leftIcon,
  rightIcon,
  rightIconRef,
  onRightIconPress,
  rightIconLabel,
  rightElement,
  clearable,
  onClear,
  showCharacterCount = false,
  prominent,
  shape,
  maxLength,
  disabled = false,
  onFocus,
  onBlur,
  onChange,
  onChangeText: userOnChangeText,
  onPress,
  value,
  defaultValue,
  placeholder,
  id: providedId,
  "aria-label": accessibilityLabel,
  ...inputProps
}, forwardedRef) {
  const inputRef = useRef<TextInput>(null);
  useImperativeHandle<TextInput | null, TextInput | null>(forwardedRef, () => inputRef.current, []);

  // Tamagui types the value as the broad HTML union; stringify for the text-only
  // tracking the shell does (label floating + character count).
  const {
    controlProps,
    shellProps,
    currentValue
  } = useTextFieldShell({
    idPrefix: "input",
    id: providedId,
    label,
    validation,
    disabled,
    value: value === undefined ? undefined : String(value),
    defaultValue: defaultValue === undefined ? undefined : String(defaultValue),
    placeholder,
    helperText,
    accessibilityLabel,
    onChangeText: userOnChangeText,
    onFocus,
    onBlur,
    controlRef: inputRef
  });
  const hasValue = Boolean(currentValue);
  const isControlled = value !== undefined;
  const handleClear = useCallback(() => {
    const node = inputRef.current;
    // Only the uncontrolled mode needs an imperative reset — React never
    // rewrites an uncontrolled control's text, so `onChangeText("")` alone
    // wouldn't clear it. Controlled owners stay authoritative via the
    // `onChangeText` below. Native RN TextInput exposes `.clear()`; the web
    // DOM <input> doesn't, so reset its `value` directly. `HTMLInputElement`
    // isn't a defined global on native, so the `instanceof` check itself must
    // be gated on `isWeb` or it throws when Clear is pressed.
    if (!isControlled) {
      if (isWeb) {
        if (node instanceof HTMLInputElement) {
          node.value = "";
        }
      } else {
        node?.clear?.();
      }
    }
    controlProps.onChangeText("");
    onClear?.();
    node?.focus();
  }, [controlProps, onClear, isControlled]);
  const isInvalid = validation === "error";

  // Trailing-slot precedence: clearable-when-value → pressable icon → decorative.
  const trailingSlot: FieldShellSlot | undefined = clearable ? hasValue && !disabled ? <FieldIconButton icon={X} label="Clear" onPress={handleClear} shape={shape} /> : undefined : onRightIconPress && rightIcon ? <FieldIconButton ref={rightIconRef} icon={rightIcon} label={rightIconLabel} onPress={onRightIconPress} disabled={disabled} color={isInvalid ? "$iconNegative" : "$iconDefault"} shape={shape} /> : rightIcon;
  return <FieldShell validation={validation} disabled={disabled} label={label} required={required} left={leftIcon} right={rightElement ?? trailingSlot} prefix={prefix} suffix={suffix} helperText={helperText} showCharacterCount={showCharacterCount} prominent={prominent} shape={shape} maxLength={maxLength} {...shellProps}
  // `pressable` gives the frame the press + pointer cursor; gate off when
  // disabled, else the pointer overrides the disabled `not-allowed` cursor.
  {...onPress ? {
    onPress,
    pressable: !disabled
  } : {}}>
      <StyledInput ref={inputRef} validation={validation} disabled={disabled} aria-required={required ? true : undefined} onChange={onChange} value={value} defaultValue={defaultValue} maxLength={maxLength} {...controlProps} {...inputProps}
    // Tamagui's native input reads `autoFocusNative` and drops `autoFocus`.
    autoFocusNative={inputProps.autoFocus} {...isWeb && inputProps.secureTextEntry ? {
      type: "password" as const
    } : {}} />
    </FieldShell>;
});